/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {toFixedVoltage} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import Device from '@ion/core/src/services/Device';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import {IndicatorStatus} from '@ion/dls/src/components/assetcard/current/vehicleTypeIndicatorConfig';
import {
  DIAGNOSTIC_TILE_LABELS,
  DIAGNOSTIC_TILE_ORDER,
  DiagnosticTileData,
  DiagnosticTileKey,
  DiagnosticTileSeverity
} from '@ion/dls/src/components/assetdetails/current/diagnosticTileConfig';
import {InputsRowData} from '@ion/dls/src/components/assetdetails/current/InputsSection';
import {getDeviceInputs} from '@ion/dls/src/components/device/DeviceInputContent';
import {getAlertIndicatorValues, getHealthIndicatorValues} from './getAssetSummaryCardProps';

// resolves an IndicatorStatus (the Asset Summary Card's own ION-7360-
// thresholded severity, shared via getHealthIndicatorValues/
// getAlertIndicatorValues) to a DiagnosticTileSeverity - the Diagnostic Tile
// Grid has no `unsupported` visual, so it falls back to `good` ...
export const toTileSeverity = (status: IndicatorStatus = 'default'): DiagnosticTileSeverity => ({
  default     : 'good',
  warning     : 'warning',
  critical    : 'critical',
  unsupported : 'good'
}[status]);

// shared by the legacy Battery Level CardItemRow (AssetDetailsDiagnostics)
// and the current theme's batteryLevel tile - int/ext are raw millivolt
// readings, converted to volts via the same `toFixedVoltage` helper
// `getBatteryVoltageFixed` uses elsewhere, fixed to 2 decimal places (the
// current theme's tile is a fixed-size 130x64px card - a longer, unrounded
// value wraps it to 3 lines) ...
export const getBatteryLevel = (int: number, ext: number) => `${toFixedVoltage(int, 2)}/${toFixedVoltage(ext, 2)}`;

// a numeric reading followed by its measuring unit (e.g. `12.7 V`, `9 %`,
// `1234.5 mi`) - the tile draws the unit smaller than the figure, so it's
// split off; text values (e.g. `Buckled`, `Engine ON`) are left whole ...
const VALUE_WITH_UNIT = /^([\d.,/-]*\d)\s*([^\d\s]+)$/;

export const splitValueUnit = (value: string, unit?: string): {value: string; unit?: string} => {
  const match = unit ? null : VALUE_WITH_UNIT.exec(value ?? '');
  return match ? {value: match[1], unit: match[2]} : {value, unit};
};

/**
 * Builds the ION-7462 Diagnostic Tile Grid's tile data from an asset plus
 * its live LMU-diagnostics values - the pure mapping pass behind
 * `useDiagnosticTileData`, extracted so it's testable directly against
 * fixtures without mounting a component or a `useMemo`.
 *
 * Mirrors the legacy `AssetDetailsDiagnostics` content field-by-field:
 * Seatbelt/Asset Battery/Fuel/Charge/Device Battery/GPS Quality reuse
 * `getHealthIndicatorValues`/`getAlertIndicatorValues` - the same vetted
 * ION-7360 thresholds the Asset Summary Card's own indicator row already
 * uses - rather than re-deriving severity here; Engine Light/Odometer/
 * Engine Hours instead reuse the live LMU-diagnostics-derived
 * `deviceDiagnostics` (fresher than the plain asset attribute) exactly as
 * the legacy CardItemRows do. Fuel/Charge have no tile-recoloring rule (per
 * `vehicleTypeIndicatorConfig`'s own "Fuel and Asset Charge... do NOT change
 * this card's icon color" note), so they're always `good`; Odometer/Engine
 * Hours have no severity source of any kind, so they're `good` too. A field
 * with no data is omitted from the array entirely, never shown as a
 * placeholder tile.
 *
 * ```typescript
 *
 *   import {buildDiagnosticTileData} from
 *     '@/routes/tracking/useDiagnosticTileData';
 *   ...
 *
 *   const tiles = buildDiagnosticTileData(asset, deviceDiagnostics);
 *
 * ```
 *
 * @param asset             the selected asset
 * @param deviceDiagnostics the live LMU-diagnostics-derived values
 * @returns The tile data, in `DIAGNOSTIC_TILE_ORDER`
 */
export const buildDiagnosticTileData = (asset: Device, deviceDiagnostics: any): DiagnosticTileData[] => {
  const healthValues = getHealthIndicatorValues(asset);
  const alertValues  = getAlertIndicatorValues(asset);
  const tileValues: Partial<Record<
  DiagnosticTileKey, {value: string; unit?: string; severity: DiagnosticTileSeverity}
  >> = {};
  if (healthValues.seatbelt) {
    tileValues.seatbelt = {value: healthValues.seatbelt.value, severity: toTileSeverity(healthValues.seatbelt.status)};
  }
  if (isTrue(deviceDiagnostics?.vbusMilStatus)) {
    tileValues.engineLight = {value: translate('tracking:assetDetail.diagnostics.engineON'), severity: 'critical'};
  }
  if (healthValues.assetBattery) {
    tileValues.assetBattery = {
      value    : healthValues.assetBattery.value,
      severity : toTileSeverity(healthValues.assetBattery.status)
    };
  }
  if (healthValues.fuel) {
    tileValues.fuel = {value: healthValues.fuel.value, severity: 'good'};
  }
  if (healthValues.charge) {
    tileValues.charge = {value: healthValues.charge.value, severity: 'good'};
  }
  // `deviceDiagnostics.odometer` is a formatted string that falls back to
  // the "N/A" placeholder (via `resolveValue`) when the raw reading isn't
  // available - checked here so that placeholder is omitted from the grid
  // entirely rather than shown as a confusing "N/A" tile ...
  if (deviceDiagnostics?.odometer && deviceDiagnostics.odometer !== translate('common:notAvailable')) {
    tileValues.odometer = {value: deviceDiagnostics.odometer, severity: 'good'};
  }
  if (deviceDiagnostics?.engineHour) {
    tileValues.engineHours = {value: deviceDiagnostics.engineHour.toString(), severity: 'good'};
  }
  if (alertValues.gpsQuality) {
    tileValues.gpsQuality = {
      value    : alertValues.gpsQuality.value,
      severity : toTileSeverity(alertValues.gpsQuality.status)
    };
  }
  if (healthValues.deviceBattery) {
    tileValues.deviceBattery = {
      value    : healthValues.deviceBattery.value,
      severity : toTileSeverity(healthValues.deviceBattery.status)
    };
  }
  if (deviceDiagnostics?.ADCDeviceBatteryVoltage && deviceDiagnostics.ADCExternalPowerVoltage) {
    tileValues.batteryLevel = {
      value    : getBatteryLevel(deviceDiagnostics.ADCDeviceBatteryVoltage, deviceDiagnostics.ADCExternalPowerVoltage),
      unit     : 'V',
      severity : 'good'
    };
  }
  return DIAGNOSTIC_TILE_ORDER
    .filter(key => tileValues[key])
    .map(key => ({
      id    : key,
      label : DIAGNOSTIC_TILE_LABELS[key],
      ...tileValues[key],
      ...splitValueUnit(tileValues[key].value, tileValues[key].unit)
    }));
};

/**
 * Memoized wrapper around `buildDiagnosticTileData`, for use directly in a
 * component's render.
 *
 * ```typescript
 *
 *   import {useDiagnosticTileData} from
 *     '@/routes/tracking/useDiagnosticTileData';
 *   ...
 *
 *   const tiles = useDiagnosticTileData(asset, deviceDiagnostics);
 *
 * ```
 *
 * @param asset             the selected asset
 * @param deviceDiagnostics the live LMU-diagnostics-derived values
 * @returns The memoized tile data
 */
export const useDiagnosticTileData = (asset: Device, deviceDiagnostics: any): DiagnosticTileData[] => useMemo(() => (
  buildDiagnosticTileData(asset, deviceDiagnostics)
), [asset, deviceDiagnostics]);

/**
 * Mirrors `DeviceInputContent`'s own `getDeviceInputs` parsing pass,
 * reshaped into the current-theme `InputsSection`'s row data - the pure
 * mapping pass behind `useInputsRowData`.
 *
 * ```typescript
 *
 *   import {buildInputsRowData} from
 *     '@/routes/tracking/useDiagnosticTileData';
 *   ...
 *
 *   const inputs = buildInputsRowData(device);
 *
 * ```
 *
 * @param device the device to read raw input attributes from
 * @returns The `InputsSection` row data
 */
export const buildInputsRowData = (device: CVSAssetType): InputsRowData[] => (
  getDeviceInputs(device).map((input, index) => ({
    id       : `${input.label}-${index}`,
    label    : input.label,
    value    : input.value,
    isActive : Boolean(input.active)
  }))
);

/**
 * Memoized wrapper around `buildInputsRowData`, for use directly in a
 * component's render.
 *
 * ```typescript
 *
 *   import {useInputsRowData} from
 *     '@/routes/tracking/useDiagnosticTileData';
 *   ...
 *
 *   const inputs = useInputsRowData(device);
 *
 * ```
 *
 * @param device the device to read raw input attributes from
 * @returns The memoized `InputsSection` row data
 */
export const useInputsRowData = (device: CVSAssetType): InputsRowData[] => useMemo(() => (
  buildInputsRowData(device)
), [device]);
